import { ControlFacet, childControlOption } from '../models/controls/ControlFacet';
import { IItemTileCellProps, IItemSetTilesListCellContent } from '../components/itemSetTilesList/ItemSetTilesList';
import { defaultRenderComponentOption } from '../components/controls/defaultRenderComponentControlOption';
import { IComponentAsProps } from '@uifabric/utilities/lib/IComponentAs';
import {
  ITilesGridSegment
} from '@uifabric/experiments/lib/TilesList';
import { Omit } from '../utilities/Omit';
import { graftControlOption } from '../models/controls/graftControlOption';
import { ISpecifiedItemProps } from '../components/decorators/selectors';
import { IDecoratedProps } from '../utilities/components/Decorators';
import { IItemTileProps, IItemTileMode } from '../components/itemTile/ItemTile';

/**
 * The props consumed by the renderer for each item in a tile list or group.
 * @public
 */
export type IItemTileCellProps = IDecoratedProps<IItemTileProps, {}, {}>;

/**
 * The props consumed by the renderer for the header in a tile group.
 * @public
 */
export type IHeaderTileCellProps = ISpecifiedItemProps;

/**
 * Provides a way to override the behavior of a tile (for layout) within a group or whole Tiles List.
 * Emit an override from `itemSetTilesListControl`.
 * @beta
 */
export const itemSetTilesListTileControl = new ControlFacet<{
  itemKey: string | undefined;
}, {
    desiredSize: {
      width: number;
      height: number;
    };
    tileMode: IItemTileMode;
  }>('itemSetTilesListTileControl');

/**
 * @beta
 */
export type ITilesListControlGridOverrides = Omit<
  ITilesGridSegment<IItemSetTilesListCellContent>,
  'key' | 'items' | 'isPlaceholder'
  >;

/**
 * @beta
 */
export interface IItemSetTilesListGroupControlProps {
  groupKey: string | undefined;
  itemKeys: string[] | undefined;
}

/**
 * @beta
 */
export type IItemSetTilesListGroupControlOverrides = {
  tileRenderer: React.ComponentType<IComponentAsProps<IItemTileCellProps>>;
  headerRenderer: React.ComponentType<IComponentAsProps<IHeaderTileCellProps>>;
  tileControlHandler: typeof itemSetTilesListTileControl.type;
  grid: ITilesListControlGridOverrides;
};

/**
 * Provides a way to override the behavior of a group within the Tiles List for a given item set.
 * Emit an override from `itemSetTilesListControl`.
 * @beta
 */
export const itemSetTilesListGroupControl = new ControlFacet<
  IItemSetTilesListGroupControlProps,
  IItemSetTilesListGroupControlOverrides>('itemSetTilesListGroupControl', {
    tileRenderer: defaultRenderComponentOption<IItemTileCellProps>(),
    headerRenderer: defaultRenderComponentOption<IHeaderTileCellProps>(),
    tileControlHandler: childControlOption(itemSetTilesListTileControl),
    grid: graftControlOption<ITilesListControlGridOverrides>()
  });

/**
 * @beta
 */
export interface IItemSetTilesListControlProps {
  itemSetKey: string | undefined;
  itemKeys: string[] | undefined;
}

/**
 * @beta
 */
export interface IItemSetTilesListControlOverrides {
  tileRenderer: React.ComponentType<IComponentAsProps<IItemTileCellProps>>;
  tileControlHandler: typeof itemSetTilesListTileControl.type;
  groupControlHandler: typeof itemSetTilesListGroupControl.type;
  grid: ITilesListControlGridOverrides;
}

/**
 * Provides a way to override the behavior of the Tiles List for a given item set.
 * @beta
 */
export const itemSetTilesListControl = new ControlFacet<
  IItemSetTilesListControlProps,
  IItemSetTilesListControlOverrides
  >('itemSetTilesListControl', {
    groupControlHandler: childControlOption(itemSetTilesListGroupControl),
    tileRenderer: defaultRenderComponentOption<IItemTileCellProps>(),
    tileControlHandler: childControlOption(itemSetTilesListTileControl),
    grid: graftControlOption<ITilesListControlGridOverrides>()
  });
